// Applies the domiciliary-schedule draft queue on top of the server snapshot
// so the board reflects unpublished edits immediately (mirrors the duty-roster
// applyDraft). Pure: returns a new data object, never mutates the input.
//
// Change types handled here:
//   assign         — move a visit to carer(s) or back to Unassigned
//   timeAway       — mark a carer away for the day (their visits go Unassigned)
//   removeTimeAway — clear a carer's time away
//   availability   — add an extra-availability band to a carer
//   stopVisit      — drop a visit from the day
//
// Touched items get a `__draft` flag so the UI can highlight them.

const clone = (obj) => (typeof structuredClone === 'function'
  ? structuredClone(obj)
  : JSON.parse(JSON.stringify(obj)));

const startMin = (t) => {
  if (!t) return 0;
  const [h, m] = String(t).split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

// Find a carer row by id across every zone (a carer can appear in more than
// one zone — return all matching rows so edits stay consistent).
const carerRows = (data, carerId) => {
  const rows = [];
  data.zones.forEach((z) => (z.carers || []).forEach((c) => {
    if (String(c.id) === String(carerId)) rows.push(c);
  }));
  return rows;
};

// Remove every instance of a visit (by assignment_id) from carers + unassigned.
// Returns a representative visit object (sans carer-specific fields) or null.
const pullVisit = (data, assignmentId) => {
  let found = null;
  data.zones.forEach((z) => (z.carers || []).forEach((c) => {
    c.visits = (c.visits || []).filter((v) => {
      if (String(v.assignment_id) === String(assignmentId)) { found = found || v; return false; }
      return true;
    });
  }));
  data.unassigned = (data.unassigned || []).filter((v) => {
    if (String(v.assignment_id) === String(assignmentId)) { found = found || v; return false; }
    return true;
  });
  return found;
};

const recomputeSummary = (data) => {
  let assigned = 0;
  let away = 0;
  data.zones.forEach((z) => (z.carers || []).forEach((c) => {
    assigned += (c.visits || []).length;
    if (c.time_away) away += 1;
  }));
  data.summary = {
    ...(data.summary || {}),
    assigned_visits: assigned,
    unassigned_visits: (data.unassigned || []).length,
    time_away: away,
  };
};

const applyOne = (data, change) => {
  const p = change.payload || {};
  switch (change.type) {
    case 'assign': {
      const base = pullVisit(data, p.assignment_id) || (p.visit ? clone(p.visit) : null);
      if (!base) return;
      const targets = p.carerIds || [];
      if (targets.length === 0) {
        data.unassigned.push({ ...base, status: undefined, reason: 'Unassigned · draft', __draft: true });
        return;
      }
      targets.forEach((t) => {
        const rows = carerRows(data, t.id);
        const visit = { ...base, status: base.status === 'completed' ? 'completed' : 'scheduled', __draft: true };
        if (rows.length) {
          rows[0].visits = [...(rows[0].visits || []), visit].sort((a, b) => startMin(a.start) - startMin(b.start));
        } else {
          // Target carer isn't rendered in any zone — keep the visit visible.
          data.unassigned.push({ ...base, reason: `Assigned to ${t.name} · draft`, __draft: true });
        }
      });
      return;
    }
    case 'timeAway': {
      const rows = carerRows(data, p.employee_id);
      rows.forEach((c) => {
        // Carries the type too, so re-opening a draft booking prefills the same
        // reason the manager just chose.
        c.time_away = {
          id: p.existingId || null,
          leave_type: p.leave_type || null,
          reason: p.reason || 'Time away',
          notes: p.notes || null,
          __draft: true,
        };
        // Whole-day away → their visits for the day return to Unassigned.
        (c.visits || []).forEach((v) => data.unassigned.push({ ...v, status: undefined, reason: `${c.name} on ${(p.reason || 'time away').toLowerCase()} · draft`, __draft: true }));
        c.visits = [];
      });
      return;
    }
    case 'removeTimeAway': {
      carerRows(data, p.employee_id).forEach((c) => { c.time_away = null; });
      return;
    }
    case 'availability': {
      carerRows(data, p.employee_id).forEach((c) => {
        c.extra_availability = [...(c.extra_availability || []), { start: p.start, end: p.end, zone_id: p.zone_id || null, note: p.note || null, __draft: true }];
      });
      return;
    }
    case 'stopVisit': {
      pullVisit(data, p.assignment_id);
      return;
    }
    case 'review': {
      // Mark a missed/differing visit resolved with recorded actual times.
      data.zones.forEach((z) => (z.carers || []).forEach((c) => (c.visits || []).forEach((v) => {
        if (String(v.assignment_id) === String(p.assignment_id)
          && (p.employee_id == null || String(c.id) === String(p.employee_id))) {
          v.actual_start = p.clock_in;
          v.actual_end = p.clock_out;
          v.status = 'completed';
          v.__reviewed = true;
          v.__draft = true;
        }
      })));
      return;
    }
    default:
  }
};

export const applyScheduleDraft = (data, changes = []) => {
  if (!data) return data;
  if (!changes.length) return data;
  const next = clone(data);
  next.zones = next.zones || [];
  next.unassigned = next.unassigned || [];
  changes.forEach((c) => applyOne(next, c));
  recomputeSummary(next);
  return next;
};

export default applyScheduleDraft;
